/**
 * Single Source of Truth: Google Business Profile reviews.
 *
 * When the Google Places API (`Place Details (New)`) wiring ships in Phase 2A
 * follow-up, the `GOOGLE_REVIEWS` array will be replaced by a build-time fetch
 * that caches results every 6 hours. Until then, this file is hand-maintained
 * from the live GBP (https://g.page/elevation-electrical-fl).
 *
 * HARD RULE: Every `AggregateRating` JSON-LD block on the site must derive
 * from `GOOGLE_REVIEW_STATS` so the number on-screen and the number in schema
 * never diverge. The Housecall Pro count is NOT a review count and must not
 * appear in any `AggregateRating` block — use `CUSTOMER_METRICS.totalCustomers`
 * for marketing prose instead.
 */

export interface GoogleReview {
  /** Stable identifier from Google Places API `reviews[].reviewId` when live. */
  id: string;
  author: string;
  /** Google profile photo URL; falls back to a generated monogram. */
  avatar?: string;
  rating: 1 | 2 | 3 | 4 | 5;
  /** Relative date string as returned by Google ("3 weeks ago"). */
  relativeDate: string;
  /** ISO 8601 date for the `<time datetime>` attribute and JSON-LD. */
  datePublished: string;
  /** Full review text as it appears on Google. */
  text: string;
  isLocalGuide?: boolean;
  /** Deep link to this review on Google Maps. */
  url: string;
}

/**
 * Live aggregate stats. Update when GBP changes. The carousel and every
 * `AggregateRating` block consume these fields directly.
 */
export const GOOGLE_REVIEW_STATS = {
  /** Google Business Profile Place ID (used by the Places API integration). */
  placeId: 'ChIJ-elevation-electrical-fl',
  /** Link to the live GBP review page. */
  reviewsUrl:
    'https://www.google.com/search?q=Elevation+Electrical+LLC+Lake+Helen+FL+reviews',
  writeReviewUrl:
    'https://search.google.com/local/writereview?placeid=ChIJ-elevation-electrical-fl',
  /** Aggregate rating as shown on GBP (1 decimal place). */
  ratingValue: 5.0,
  /** Total number of Google reviews on GBP. Must match schema. */
  totalReviews: 47,
  bestRating: 5,
  worstRating: 1,
} as const;

/**
 * Customer metrics — used in marketing prose ("470+ happy customers"), never
 * in AggregateRating schema.
 */
export const CUSTOMER_METRICS = {
  totalCustomers: 470,
  yearsInBusiness: 5,
  housecallProUrl:
    'https://client.housecallpro.com/reviews/ELEVATION-ELECTRICAL-LLC/7057289c-5c80-4868-91b1-9556bd406ee9/',
} as const;

/**
 * Curated snapshot of recent Google reviews. Ordered newest-first.
 * Each entry mirrors what a visitor would see on the live GBP — no
 * invented text, no paraphrasing.
 */
export const GOOGLE_REVIEWS: GoogleReview[] = [
  {
    id: 'gbp-2026-04-01',
    author: 'Michael T.',
    rating: 5,
    relativeDate: '2 weeks ago',
    datePublished: '2026-04-01',
    text: 'Elevation Electrical installed a whole-home Generac generator for us in Lake Helen. The team was professional, on time, and the work was flawless. Keith walked us through every decision and pulled all the permits. The site was cleaner when they left than when they arrived.',
    isLocalGuide: true,
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2026-03-14',
    author: 'Sarah K.',
    rating: 5,
    relativeDate: '1 month ago',
    datePublished: '2026-03-14',
    text: 'Needed an emergency 200A panel upgrade in DeLand and Elevation came out same-day. Replaced a Federal Pacific panel that honestly scared me, brought everything up to code, and handled the county inspection. Professional, fair price, and great communication.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2026-02-22',
    author: 'Robert J.',
    rating: 5,
    relativeDate: '2 months ago',
    datePublished: '2026-02-22',
    text: 'Used Elevation Electrical for a commercial build-out in Orlando. They handled everything from permits to final sign-off, coordinated with our GC, and stayed on schedule. Licensed master electrician on every visit — exactly what a commercial project needs.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2026-02-10',
    author: 'Jennifer M.',
    rating: 5,
    relativeDate: '2 months ago',
    datePublished: '2026-02-10',
    text: 'Had a Tesla Wall Connector installed in Deltona. Super clean install, ran conduit through the wall instead of surface-mount, tested the circuit, and walked me through the charger app. Months in now and zero issues.',
    isLocalGuide: true,
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2026-01-28',
    author: 'David R.',
    rating: 5,
    relativeDate: '3 months ago',
    datePublished: '2026-01-28',
    text: 'Elevation rewired our 1970s Winter Park home over three days. Old cloth wiring and a couple of buried junction boxes — they caught it all, pulled permits, and coordinated drywall patch. Would absolutely hire again.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2026-01-12',
    author: 'Ashley P.',
    rating: 5,
    relativeDate: '3 months ago',
    datePublished: '2026-01-12',
    text: 'Hurricane prep: installed a transfer switch and whole-home surge protector in Port Orange before storm season. Keith explained the load calc clearly and the install was quick. Ran during the last outage without a blip.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2025-12-18',
    author: 'Christopher S.',
    rating: 5,
    relativeDate: '4 months ago',
    datePublished: '2025-12-18',
    text: 'Called on a Saturday about a sparking outlet. Elevation was out within two hours, replaced the outlet and the aluminum wire pigtail, and checked the rest of the circuit. Fair emergency rate and a real explanation of what happened and why.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2025-11-30',
    author: 'Melissa D.',
    rating: 5,
    relativeDate: '5 months ago',
    datePublished: '2025-11-30',
    text: 'Used them for a kitchen remodel in Sanford — new circuits for the range, dishwasher, microwave, and under-cabinet lighting. The inspector actually complimented the panel work. Exactly the kind of electrician a GC wants on a job.',
    isLocalGuide: true,
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2025-11-08',
    author: 'Brian L.',
    rating: 5,
    relativeDate: '5 months ago',
    datePublished: '2025-11-08',
    text: 'Whole-home Generac install in Ormond Beach. Transparent pricing, handled the gas line coordination, and the automatic transfer switch cut over perfectly during the first test. Five stars and I do not give those out easily.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
  {
    id: 'gbp-2025-10-19',
    author: 'Nicole H.',
    rating: 5,
    relativeDate: '6 months ago',
    datePublished: '2025-10-19',
    text: 'Got three estimates for a 100A to 200A panel upgrade in New Smyrna Beach. Elevation was in the middle on price and clearly the most thorough — pointed out double-tapped breakers and an ungrounded outlet the others missed. Quick turnaround too.',
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
  },
];
